import {Component, inject, input} from '@angular/core';
import {output} from '@angular/core';
import {StoreEmployeeRequirement} from '../../../classes/models/store/StoreEmployeeRequirement';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {EmployeeRequirementsFormComponent} from './employee-requirements-form/employee-requirements-form.component';
import {DataService} from '../../../services/store/data.service';
import {TableHeader} from '../../../shared/classes/TableHeader';
import {
  EmployeeRequirementsDeleteComponent
} from './employee-requirements-delete/employee-requirements-delete.component';
import {WorkingHoursService} from '../../../services/store/working-hours.service';
import {SHARED_IMPORTS} from "../../../shared/standalone-imports";

/**
 * This component handles displaying and editing store requirements.
 */
@Component({
  selector: 'app-employee-requirements',
  templateUrl: './employee-requirements.component.html',
  styleUrls: ['./employee-requirements.component.less'],
  imports: [SHARED_IMPORTS]
})
export class EmployeeRequirementsComponent {
  private modalService = inject(NgbModal);
  private dataService = inject(DataService);
  private workingHoursService = inject(WorkingHoursService);

  /** An identifier of a store. */
  readonly storeId = input<string | undefined>(undefined);
  /** A list of store requirements. */
  readonly requirements = input<StoreEmployeeRequirement[]>([]);
  /** An event to refresh store page data. */
  readonly getStoreDetailsEvent = output();

  /** Headers of a store requirements table. */
  headers: TableHeader[] = [
    new TableHeader('Opening Time', 'minimumOpeningEmployees', false),
    new TableHeader('Regular Time', 'minimumRegularEmployees', false),
    new TableHeader('Closing Time', 'minimumClosingEmployees', false),
    new TableHeader('Start Date', 'startDate', false),
    new TableHeader('', '', false)
  ];

  /**
   * Checks if data is allowed to be edited at the given date.
   * @param date the date, at which data might be edited.
   */
  canEditAtDate(date: string): boolean {
    return this.dataService.canEditAtDate(date);
  }

  /**
   * Get extra time in minutes before opening and after closing the store.
   */
  getExtraTime(): number {
    return this.workingHoursService.EXTRA_TIME;
  }

  /**
   * Opens a modal window to create a new store requirement.
   */
  openCreate(): void {
    const modalRef = EmployeeRequirementsFormComponent.open(this.modalService, {
      storeId: this.storeId() ?? '',
      previousRequirement: undefined,
      weekExists: false
    });
    modalRef.result.then(() => {
      this.getStoreDetailsEvent.emit();
    });
  }

  /**
   * Opens a modal window to update an employee store requirement.
   */
  openUpdate(requirement: StoreEmployeeRequirement): void {
    const modalRef = EmployeeRequirementsFormComponent.open(this.modalService, {
      storeId: this.storeId() ?? '',
      previousRequirement: requirement,
      weekExists: true
    });
    modalRef.result.then(() => {
      this.getStoreDetailsEvent.emit();
    });
  }

  /**
   * Opens a modal window to delete an existing store requirement.
   * @param date a date from which store requirement should be deleted.
   */
  openDelete(date: string): void {
    const modalRef = EmployeeRequirementsDeleteComponent.open(this.modalService, {
      storeId: this.storeId(),
      date
    });
    modalRef.result.then(() => {
      this.getStoreDetailsEvent.emit();
    });
  }
}